import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskedLines } from "./Motion";
import { REGISTER_URL } from "@/lib/api";

const SRC = {
  mobile: { video: "/brand/hero-mobile.mp4", poster: "/brand/poster-mobile.jpg" },
  desktop: { video: "/brand/hero-desktop.mp4", poster: "/brand/poster-desktop.jpg" },
};

const useCountdown = (iso) => {
  const [t, setT] = useState({ d: 0, h: 0, m: 0 });
  useEffect(() => {
    const tick = () => { const d = Math.max(0, new Date(iso) - Date.now()); setT({ d: Math.floor(d / 864e5), h: Math.floor(d / 36e5) % 24, m: Math.floor(d / 6e4) % 60 }); };
    tick(); const i = setInterval(tick, 30000); return () => clearInterval(i);
  }, [iso]);
  return t;
};

const useMedia = () => {
  const [kind, setKind] = useState(() => (window.matchMedia("(min-width: 768px)").matches ? "desktop" : "mobile"));
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const upd = () => setKind(mq.matches ? "desktop" : "mobile");
    mq.addEventListener("change", upd); return () => mq.removeEventListener("change", upd);
  }, []);
  return kind;
};

const Stat = ({ v, l, delay, gold }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="border-l border-[#D4AF37]/30 pl-3 sm:pl-5">
    <p className={`font-display text-2xl sm:text-3xl lg:text-4xl tabular-nums ${gold ? "text-[#D4AF37]" : "text-[#F8F6F0]"}`}>{v}</p>
    <p className="text-[10px] tracking-[0.18em] uppercase text-[#6F7C97] mt-1">{l}</p>
  </motion.div>
);

export const Hero = ({ auctionDate = "2026-12-28T18:00:00Z" }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const kind = useMedia();
  const media = SRC[kind];
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const mediaY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const c = useCountdown(auctionDate);

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] overflow-hidden flex items-end bg-[#0B1630]" data-testid="hero-section">
      <motion.div style={{ y: mediaY }} className="absolute inset-0">
        <motion.img src={media.poster} alt="Ballypatrick auction horse jumping in the Ballypatrick arena" fetchPriority="high"
          className="h-full w-full object-cover object-[55%_30%]" initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }} />
        <video key={kind} poster={media.poster} autoPlay muted loop playsInline preload="auto"
          onPlaying={() => setPlaying(true)} onError={() => setPlaying(false)}
          className={`absolute inset-0 h-full w-full object-cover object-[55%_30%] transition-opacity duration-1000 ${playing ? "opacity-100" : "opacity-0"}`} data-testid="hero-video" data-variant={kind}>
          <source src={media.video} type="video/mp4" />
          <source src={media.video.replace(".mp4", ".webm")} type="video/webm" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1630] via-[#0B1630]/65 to-[#0B1630]/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1630]/80 via-[#0B1630]/30 to-transparent" />
      </motion.div>


      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1400px] px-5 sm:px-8 lg:px-12 pb-10 sm:pb-16 pt-32">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }} className="eyebrow mb-4 sm:mb-8" data-testid="hero-eyebrow">
          Auction V · 28 Dec 2026 · Online
        </motion.p>
        <MaskedLines lines={["Seven generations.", "One standard.", "Proven in the ring."]} delay={0.45}
          className="font-display font-medium text-[#F8F6F0] text-[clamp(2.5rem,9vw,6.5rem)] leading-[0.98] tracking-tight max-w-5xl" />
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }} className="mt-5 max-w-md text-sm sm:text-base text-[#AEB7C9] leading-relaxed" data-testid="hero-subline">
          Ireland's most selective sport horse auction. Every lot personally chosen by Olympian Greg Broderick.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9 }} className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
          <a href={REGISTER_URL} target="_blank" rel="noreferrer" className="btn-gold" data-testid="ballypatrick-btn-register-bid-hero">Register to bid <ArrowUpRight size={15} /></a>
          <a href="#collection" className="btn-ghost" data-testid="ballypatrick-btn-explore-collection-hero">Explore the collection <ArrowDown size={15} /></a>
        </motion.div>

        <div className="mt-10 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-5 lg:gap-12">
          <Stat v="€8.2M+" l="Total sales" delay={1.5} />
          <Stat v="75+" l="Championship horses" delay={1.6} />
          <Stat v="37+" l="Countries sold to" delay={1.7} />
          <div data-testid="ballypatrick-countdown-timer-wrapper">
            <Stat gold delay={1.8} l="Until bidding opens" v={<>{c.d}<span className="text-base text-[#6F7C97]">d </span>{String(c.h).padStart(2, "0")}<span className="text-base text-[#6F7C97]">h </span>{String(c.m).padStart(2, "0")}<span className="text-base text-[#6F7C97]">m</span></>} />
          </div>
        </div>
      </motion.div>
    </section>
  );
};
